import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import type { MapMove, MapPing } from '../types';
import { decodePolyline } from '@utils/decodePolyline';
import { haversineDistance, pointToPolylineDistance } from '@utils/geoUtils';

dayjs.extend(duration);

// CONSTANTS ----------------------------------------------------------------- //

const MILES_TO_METERS = 1609.34;

// TYPES --------------------------------------------------------------------- //

export interface ProgressData {
  percentage: number;
  remainingMeters: number;
}

// PURE FUNCTIONS ------------------------------------------------------------ //

/**
 * Compute how far along a route a driver has progressed, based on
 * the latest GPS ping projected onto the route polyline.
 */
export function computeProgress(
  move: MapMove,
  movePings: MapPing[]
): ProgressData | null {
  const polylineStr = move.lane?.route_polyline;
  if (!polylineStr) return null;

  const polyline = decodePolyline(polylineStr);
  if (polyline.length < 2) return null;

  if (movePings.length === 0) return null;
  const latestPing = movePings[movePings.length - 1];
  const pingCoords = latestPing.location?.coordinates;
  if (!pingCoords || pingCoords.length < 2) return null;

  const pingPoint = { lat: pingCoords[1], lng: pingCoords[0] };

  // Total route distance
  let totalRouteDistance: number;
  if (move.lane?.distance_miles != null && move.lane.distance_miles > 0) {
    totalRouteDistance = move.lane.distance_miles * MILES_TO_METERS;
  } else {
    totalRouteDistance = 0;
    for (let i = 0; i < polyline.length - 1; i++) {
      totalRouteDistance += haversineDistance(polyline[i], polyline[i + 1]);
    }
  }
  if (totalRouteDistance <= 0) return null;

  // Project ping onto polyline and find the nearest segment
  const { nearest } = pointToPolylineDistance(pingPoint, polyline);

  // Sum distance from start to the projected point
  let distanceTraveled = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const segDist = haversineDistance(polyline[i], polyline[i + 1]);
    const distToNearest = haversineDistance(polyline[i], nearest);
    const distFromNearestToNext = haversineDistance(nearest, polyline[i + 1]);

    // Check if nearest lies on this segment (within tolerance)
    if (Math.abs(distToNearest + distFromNearestToNext - segDist) < 1) {
      distanceTraveled += distToNearest;
      break;
    }
    distanceTraveled += segDist;
  }

  const percentage = Math.min(100, Math.max(0, (distanceTraveled / totalRouteDistance) * 100));
  const remainingMeters = totalRouteDistance * (1 - percentage / 100);

  return { percentage, remainingMeters };
}

/**
 * Compute an ETA string from remaining distance and current speed.
 */
export function computeEta(remainingMeters: number, speedMs?: number): string {
  if (speedMs == null || speedMs <= 0) return '-';

  const remainingSeconds = remainingMeters / speedMs;
  const dur = dayjs.duration(remainingSeconds, 'seconds');
  const totalMinutes = Math.round(dur.asMinutes());

  if (totalMinutes < 1) return '< 1 min remaining';
  if (totalMinutes < 60) return `~${totalMinutes} min remaining`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return `~${hours} hr remaining`;
  return `~${hours} hr ${mins} min remaining`;
}
